import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// Notification type for toast messages
export interface ToastNotification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  onRetry?: () => void | Promise<void>
  onAction?: () => void | Promise<void>
  actionLabel?: string
}

// Right-hand work panel on the chat page (UI-only; the data it shows lives in TanStack Query)
export interface WorkPanelState {
  isCollapsed: boolean
  /** A chat page with the docked panel is on screen (the Work button toggles it there). */
  docked: boolean
}

// Chat panel state
export interface ChatPanelState {
  isOpen: boolean
}

/** The left panel's segment: the agent threads or the page list. */
export type NavSegment = 'agents' | 'pages'

// UI store state interface
interface UiState {
  navSegment: NavSegment
  workPanel: WorkPanelState
  chatPanel: ChatPanelState
  toasts: ToastNotification[]
}

// UI store actions interface
interface UiActions {
  // Work panel actions
  toggleWorkPanel: () => void
  setWorkPanelDocked: (docked: boolean) => void

  // Chat panel actions
  setChatPanelOpen: (isOpen: boolean) => void

  setNavSegment: (segment: NavSegment) => void

  // Toast actions
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
  dismissToast: (id: string) => void
}

// Combined store type
type UiStore = UiState & UiActions

// Initial state
const initialState: UiState = {
  navSegment: 'agents',
  workPanel: {
    isCollapsed: false,
    docked: false,
  },
  chatPanel: {
    isOpen: false,
  },
  toasts: [],
}

// Generate unique ID for toasts
const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

// Create the UI store with devtools middleware
export const useUiStore = create<UiStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setNavSegment: (segment: NavSegment) => set({ navSegment: segment }),

      // Work panel actions
      toggleWorkPanel: () => {
        set((state) => ({
          workPanel: { ...state.workPanel, isCollapsed: !state.workPanel.isCollapsed },
        }))
      },
      setWorkPanelDocked: (docked: boolean) => {
        set((state) => ({ workPanel: { ...state.workPanel, docked } }))
      },

      // Chat panel actions
      setChatPanelOpen: (isOpen: boolean) => {
        set((state) => ({
          chatPanel: { ...state.chatPanel, isOpen },
        }))
      },

      // Toast actions
      showToast: (toast: Omit<ToastNotification, 'id'>) => {
        // Collapse identical concurrent toasts (e.g. several requests failing
        // the same way at once) into the one already on screen.
        const duplicate = get().toasts.some(
          (t) => t.type === toast.type && t.title === toast.title && t.message === toast.message
        )
        if (duplicate) return

        const id = generateId()
        const newToast: ToastNotification = { ...toast, id }
        set((state) => ({
          toasts: [...state.toasts, newToast],
        }))

        // Auto-dismiss after duration (default 5 seconds)
        const duration = toast.duration ?? 5000
        if (duration > 0) {
          setTimeout(() => {
            get().dismissToast(id)
          }, duration)
        }
      },

      dismissToast: (id: string) => {
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        }))
      },

      // Mobile menu actions
    }),
    { name: 'UiStore', enabled: import.meta.env.DEV }
  )
)

// Selectors for common access patterns
export const selectIsWorkPanelCollapsed = (state: UiStore) => state.workPanel.isCollapsed
export const selectIsChatPanelOpen = (state: UiStore) => state.chatPanel.isOpen
export const selectToasts = (state: UiStore) => state.toasts
